/**
 * Syntaxe IMAP : découpage des réponses en jetons, encodages des noms de
 * dossiers (UTF-7 modifié) et des en-têtes (mots encodés MIME).
 *
 * Fonctions pures, sans réseau : testées par test/syntaxe.test.js.
 */

// Marque, dans le texte d'une réponse, l'emplacement d'un littéral {n}.
export const MARQUE_LITTERAL = '\u0001';

/**
 * Découpe une réponse en jetons :
 *   - liste parenthésée -> tableau
 *   - atome NIL -> null
 *   - chaîne entre guillemets -> string
 *   - littéral -> Uint8Array (octets bruts)
 *   - autre atome -> string. Un atome peut contenir des crochets, avec espaces
 *     et parenthèses à l'intérieur : BODY[HEADER.FIELDS (LIST-UNSUBSCRIBE)].
 */
export function decouper(texte, litteraux = []) {
  let i = 0;
  let prochainLitteral = 0;
  const racine = [];
  const pile = [racine];

  while (i < texte.length) {
    const c = texte[i];
    const courant = pile[pile.length - 1];

    if (c === ' ' || c === '\r' || c === '\n') { i++; continue; }
    if (c === '(') { const liste = []; courant.push(liste); pile.push(liste); i++; continue; }
    if (c === ')') { if (pile.length > 1) pile.pop(); i++; continue; }
    if (c === MARQUE_LITTERAL) { courant.push(litteraux[prochainLitteral++]); i++; continue; }

    if (c === '"') {
      let valeur = '';
      i++;
      while (i < texte.length && texte[i] !== '"') {
        if (texte[i] === '\\' && i + 1 < texte.length) i++;
        valeur += texte[i++];
      }
      i++; // guillemet fermant
      courant.push(valeur);
      continue;
    }

    let debut = i;
    let crochets = 0;
    while (i < texte.length) {
      const d = texte[i];
      if (d === '[') crochets++;
      else if (d === ']') crochets = Math.max(0, crochets - 1);
      else if (!crochets && (d === ' ' || d === '(' || d === ')' || d === MARQUE_LITTERAL)) break;
      i++;
    }
    const atome = texte.slice(debut, i);
    courant.push(atome.toUpperCase() === 'NIL' ? null : atome);
  }
  return racine;
}

const decodeurUtf8 = new TextDecoder();

/** Valeur d'un jeton sous forme de texte (un littéral est décodé en UTF-8). */
export function enTexte(jeton) {
  if (jeton === null || jeton === undefined) return null;
  if (jeton instanceof Uint8Array) return decodeurUtf8.decode(jeton);
  return String(jeton);
}

// ---------------------------------------------------------------------------
// Chaînes envoyées au serveur.
// ---------------------------------------------------------------------------

const encodeur = new TextEncoder();

/**
 * Une chaîne IMAP : entre guillemets si elle est en ASCII simple, sinon en
 * littéral (octets UTF-8). Renvoie un fragment de commande.
 */
export function chaine(s) {
  const texte = String(s);
  if (/^[\x20-\x7e]*$/.test(texte)) {
    return `"${texte.replace(/[\\"]/g, (c) => `\\${c}`)}"`;
  }
  return { litteral: encodeur.encode(texte) };
}

export function estAscii(s) {
  return /^[\x00-\x7f]*$/.test(String(s));
}

/** 1,2,3,7,8,10 -> "1:3,7:8,10". Les UID doivent être triés. */
export function ensembleUid(uids) {
  const tries = [...uids].sort((a, b) => a - b);
  const morceaux = [];
  for (let i = 0; i < tries.length; i++) {
    const debut = tries[i];
    while (i + 1 < tries.length && tries[i + 1] === tries[i] + 1) i++;
    morceaux.push(debut === tries[i] ? String(debut) : `${debut}:${tries[i]}`);
  }
  return morceaux.join(',');
}

const MOIS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Date au format de SEARCH SINCE : 1-Jan-2020. */
export function dateRecherche(date) {
  const d = new Date(date);
  return `${d.getDate()}-${MOIS[d.getMonth()]}-${d.getFullYear()}`;
}

/** INTERNALDATE (« 17-Jul-1996 02:44:25 -0700 ») -> Date, ou null. */
export function lireDateInterne(texte) {
  if (!texte) return null;
  const d = new Date(String(texte).replace(/^(\s*\d+)-(\w+)-(\d+)/, '$1 $2 $3'));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date d'en-tête (RFC 2822) -> Date, ou null si illisible. */
export function lireDateEnTete(texte) {
  if (!texte) return null;
  const propre = String(texte).replace(/\([^)]*\)/g, '').trim();
  const d = new Date(propre);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ---------------------------------------------------------------------------
// Noms de dossiers : UTF-7 modifié (RFC 3501 §5.1.3).
// « Messages envoy&AOk-s » <-> « Messages envoyés »
// ---------------------------------------------------------------------------

export function decoderUtf7(nom) {
  return String(nom).replace(/&([^-]*)-/g, (_, b64) => {
    if (!b64) return '&';
    const binaire = atob(b64.replace(/,/g, '/') + '==='.slice((b64.length + 3) % 4));
    let sortie = '';
    for (let i = 0; i + 1 < binaire.length; i += 2) {
      sortie += String.fromCharCode((binaire.charCodeAt(i) << 8) | binaire.charCodeAt(i + 1));
    }
    return sortie;
  });
}

export function encoderUtf7(nom) {
  return String(nom).replace(/&|[^\x20-\x7e]+/g, (morceau) => {
    if (morceau === '&') return '&-';
    // Unités UTF-16 big-endian : les caractères hors plan de base sont déjà,
    // dans une chaîne JS, des paires de substitution.
    let binaire = '';
    for (let j = 0; j < morceau.length; j++) {
      const u = morceau.charCodeAt(j);
      binaire += String.fromCharCode(u >> 8, u & 0xff);
    }
    return `&${btoa(binaire).replace(/=+$/, '').replace(/\//g, ',')}-`;
  });
}

// ---------------------------------------------------------------------------
// Mots encodés MIME (RFC 2047) : =?UTF-8?B?...?= et =?ISO-8859-1?Q?...?=
// ---------------------------------------------------------------------------

function octetsDepuisBase64(b64) {
  const binaire = atob(b64.replace(/[^A-Za-z0-9+/]/g, '') + '==='.slice((b64.length + 3) % 4));
  return Uint8Array.from(binaire, (c) => c.charCodeAt(0));
}

function octetsDepuisQ(q) {
  const octets = [];
  for (let i = 0; i < q.length; i++) {
    const c = q[i];
    if (c === '_') octets.push(0x20);
    else if (c === '=' && /^[0-9A-Fa-f]{2}$/.test(q.slice(i + 1, i + 3))) {
      octets.push(parseInt(q.slice(i + 1, i + 3), 16));
      i += 2;
    } else octets.push(c.charCodeAt(0) & 0xff);
  }
  return new Uint8Array(octets);
}

function decoderOctets(octets, jeu) {
  try {
    return new TextDecoder(jeu.toLowerCase(), { fatal: false }).decode(octets);
  } catch {
    return decodeurUtf8.decode(octets); // jeu de caractères inconnu du navigateur
  }
}

const MOT_ENCODE = /=\?([^?\s]+)\?([BbQq])\?([^?\s]*)\?=/g;

export function decoderMotsMime(texte) {
  if (!texte || !String(texte).includes('=?')) return texte;
  // Des mots encodés séparés seulement par des blancs se recollent sans espace.
  const recolle = String(texte).replace(/(\?=)\s+(?==\?)/g, '$1');
  // Les morceaux consécutifs d'un même jeu de caractères sont décodés ensemble :
  // un caractère multi-octets peut être coupé entre deux mots.
  let sortie = '';
  let attente = null; // { jeu, octets[] }
  let dernier = 0;
  const vider = () => {
    if (!attente) return;
    const total = new Uint8Array(attente.octets.reduce((n, o) => n + o.length, 0));
    let k = 0;
    for (const o of attente.octets) { total.set(o, k); k += o.length; }
    sortie += decoderOctets(total, attente.jeu);
    attente = null;
  };
  for (const m of recolle.matchAll(MOT_ENCODE)) {
    if (m.index > dernier) { vider(); sortie += recolle.slice(dernier, m.index); }
    const jeu = m[1].split('*')[0];
    const octets = m[2].toUpperCase() === 'B' ? octetsDepuisBase64(m[3]) : octetsDepuisQ(m[3]);
    if (attente && attente.jeu.toLowerCase() === jeu.toLowerCase()) attente.octets.push(octets);
    else { vider(); attente = { jeu, octets: [octets] }; }
    dernier = m.index + m[0].length;
  }
  vider();
  return sortie + recolle.slice(dernier);
}

// ---------------------------------------------------------------------------
// Réponses FETCH.
// ---------------------------------------------------------------------------

function lireAdresses(liste) {
  if (!Array.isArray(liste)) return [];
  return liste
    .filter(Array.isArray)
    .map(([nom, , boite, hote]) => {
      const b = enTexte(boite);
      const h = enTexte(hote);
      return {
        name: decoderMotsMime(enTexte(nom) || '') || '',
        address: b && h ? `${b}@${h}` : (b || '')
      };
    });
}

/** ENVELOPE -> la même forme qu'imapflow (date, subject, from[], messageId). */
export function lireEnveloppe(e) {
  if (!Array.isArray(e)) return null;
  return {
    date: lireDateEnTete(enTexte(e[0])),
    subject: decoderMotsMime(enTexte(e[1]) || '') || '',
    from: lireAdresses(e[2]),
    sender: lireAdresses(e[3]),
    replyTo: lireAdresses(e[4]),
    to: lireAdresses(e[5]),
    inReplyTo: enTexte(e[8]),
    messageId: enTexte(e[9])
  };
}

/**
 * Le contenu d'une réponse « * n FETCH (...) » sous forme d'objet :
 * { seq, uid, internalDate, envelope, headers }.
 */
export function lireFetch(jetons) {
  const [, seq, , donnees] = jetons;
  const msg = { seq: Number(seq) };
  if (!Array.isArray(donnees)) return msg;
  for (let i = 0; i + 1 < donnees.length; i += 2) {
    const cle = String(donnees[i]).toUpperCase();
    const valeur = donnees[i + 1];
    if (cle === 'UID') msg.uid = Number(valeur);
    else if (cle === 'INTERNALDATE') msg.internalDate = lireDateInterne(enTexte(valeur));
    else if (cle === 'ENVELOPE') msg.envelope = lireEnveloppe(valeur);
    else if (cle === 'FLAGS') msg.flags = new Set((Array.isArray(valeur) ? valeur : []).map(String));
    else if (cle.startsWith('BODY[')) msg.headers = enTexte(valeur) || '';
  }
  return msg;
}
